import styled from 'styled-components';
import { Button } from '../../../Button';
import { CardStatus } from '../../enums/CardStatus';

export const TrainingCard = styled.div<{ $wrap?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: ${({ $wrap }) => ($wrap ? 'wrap' : 'nowrap')};
  gap: 8px;
  padding: 16px;
  border-radius: ${({ theme }) => theme.borderRadius.normal};
  background-color: ${({ theme }) => theme.colors.trainingCardBackground};

  .plus-circled-icon {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const IconContainer = styled.div`
  width: 24px;
  height: 24px;
`;

export const Title = styled.span`
  color: ${({ theme }) => theme.colors.heading};
  display: block;
  font-size: 14px;
  font-weight: 600;
  min-width: 0;
  margin-right: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TrainingPrimaryButton = styled(Button).attrs({ size: 'small' })`
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryContrast};
  font-size: 12px;
  transition: all 250ms ease-in-out;

  &:hover {
    filter: brightness(0.8);
  }

  @media (max-width: 768px) {
    padding: 16px 24px;
  }
`;

export const TrainingSecondaryButton = styled(Button).attrs({ size: 'small' })<{
  $isLoading?: boolean;
}>`
  padding: 6px 12px;
  background-color: ${({ theme, $isLoading }) =>
    $isLoading === undefined ? theme.colors.primary : theme.colors.trainingCardButton};
  color: ${({ theme, $isLoading }) => ($isLoading === undefined ? theme.colors.primaryContrast : theme.colors.primary)};
  font-size: 12px;
  transition: all 250ms ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primaryContrast};
  }
`;

export const ProgressBarContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  gap: 8px;
`;

export const ProgressText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.normal};
`;

export const ProgressBar = styled.div`
  flex-grow: 1;
  height: 8px;
  border: 1px solid ${({ theme }) => theme.colors.tableBorder};
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.panelBackground};
  overflow: hidden;

  @media (max-width: 768px) {
    height: 16px;
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: unset;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 16px;
  margin-right: auto;
  min-width: 0;
`;

export const MainTrainingCard = styled(TrainingCard).withConfig({
  shouldForwardProp: (prop) => prop !== 'cardStatus',
})<{ cardStatus: CardStatus }>`
  flex-direction: column;
  align-items: unset;
  background-color: ${({ cardStatus, theme }) =>
    cardStatus === CardStatus.urgent
      ? theme.colors.urgentBackground
      : cardStatus === CardStatus.warning
        ? theme.colors.warningBackground
        : theme.colors.trainingCardBackground};

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

export const DateLabelContainer = styled.label.withConfig({
  shouldForwardProp: (prop) => prop !== 'cardStatus',
})<{ cardStatus: CardStatus }>`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  color: ${({ cardStatus, theme }) =>
    cardStatus === CardStatus.urgent
      ? theme.colors.urgent
      : cardStatus === CardStatus.warning
        ? theme.colors.waningText
        : theme.colors.normal};

  @media (max-width: 768px) {
    gap: 16px;
    align-items: center;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

export const DateLabel = styled.div`
  font-size: 12px;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

export const ProgressFill = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress' && prop !== 'backgroundColor',
})<{ progress: number; backgroundColor?: string }>`
  height: 100%;
  width: ${({ progress }) => `${progress}%`};
  background: ${({ backgroundColor, theme }) => backgroundColor || theme.colors.primary};
  transition: width 0.3s ease-in-out;
`;
